import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { candidateApi } from "@/lib/candidateApi";
import { useInvite } from "@/hooks/useInvite";
import { CandidateFlow } from "@/shell/CandidateFlow";
import { Button, Card, Label, Spinner, Alert } from "@/components/primitives";
import type { ProfileData, ProfileUploadResult } from "@/lib/types";

// Profile page (Layer 1 of the KB two-layer split). Candidates upload their
// master CV here — it persists across all sessions and invites. Per-session
// CV overrides are handled in Setup.tsx (Layer 2).

function stalenessColour(days: number | null): string {
  if (!days || days <= 30) return "text-slate-400";
  if (days <= 90) return "text-amber-600";
  return "text-red-600";
}

function stalenessCopy(days: number | null): string {
  if (!days || days <= 30) return `Updated ${days ?? 0} day${days === 1 ? "" : "s"} ago`;
  if (days <= 60) return "Updated 6 weeks ago — worth a refresh before your next session";
  if (days <= 90) return "Your CV is 2 months old. Recruiters notice currency.";
  return `Your CV hasn't been updated in ${days} days. An outdated CV costs you points before you've said a word.`;
}

export function CandidateProfile() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { data: invite } = useInvite(token);

  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!token) return;
    let live = true;
    candidateApi.getProfile(token).then((r) => {
      if (!live) return;
      setLoading(false);
      if (r.ok) setProfile(r as ProfileData);
    });
    return () => { live = false; };
  }, [token]);

  async function upload(f: File) {
    if (!token) return;
    setErr(null);
    setSuccessMsg(null);
    setUploading(true);
    const fd = new FormData();
    fd.append("cv_file", f);
    const r = await candidateApi.updateProfile(token, fd);
    setUploading(false);
    if (r.ok) {
      const res = r as ProfileUploadResult;
      setProfile({ ok: true, hasCv: true, filename: res.filename, uploadedAt: res.uploadedAt, stalenessDays: 0 });
      setSuccessMsg(`CV updated — ${res.filename}`);
    } else {
      setErr(r.error ?? "Upload failed. Please try again.");
    }
  }

  const agencyProps = {
    agencyName: invite?.agencyName,
    logoUrl: invite?.branding?.logoUrlLight || undefined,
    logoFilter: invite?.branding?.logoFilter,
  };

  if (loading) {
    return (
      <CandidateFlow {...agencyProps}>
        <div className="flex justify-center py-10"><Spinner size={28} /></div>
      </CandidateFlow>
    );
  }

  const hasCv = profile?.hasCv ?? false;
  const filename = profile?.filename;
  const stDays = profile?.stalenessDays ?? null;

  return (
    <CandidateFlow {...agencyProps}>
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-ink"
      >
        <span aria-hidden="true">←</span> Back
      </button>
      <h1 className="mb-1 text-xl font-semibold text-ink">Your profile CV</h1>
      <p className="mb-6 text-sm text-slate-500">
        Uploaded once, used across every session. Keep it current — your coach reads it before every interview.
      </p>

      {successMsg && <Alert variant="ok" className="mb-4">{successMsg}</Alert>}
      {err && <Alert variant="error" className="mb-4">{err}</Alert>}

      {hasCv ? (
        <Card className="mb-5 space-y-4 p-5">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-good-50">
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M5 10.5l3.5 3.5L15 6.5" stroke="#22A06B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-ink">Profile ready</div>
              {filename && (
                <div className="mt-0.5 truncate font-mono text-[11px] text-slate-500">{filename}</div>
              )}
              {stDays !== null && (
                <div className={`mt-1 text-xs font-medium ${stalenessColour(stDays)}`}>
                  {stalenessCopy(stDays)}
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-hair pt-3">
            <Label>Replace CV</Label>
            <p className="mb-2 text-xs text-slate-500">Upload a new version to keep your profile current.</p>
            <DropZone
              uploading={uploading}
              drag={drag}
              setDrag={setDrag}
              fileInput={fileInput}
              onFile={upload}
            />
          </div>
        </Card>
      ) : (
        <Card className="mb-5 p-5">
          <div className="mb-4 text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-paper-3">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="var(--slate-400)" strokeWidth="1.5" strokeLinejoin="round" />
                <polyline points="14,2 14,8 20,8" stroke="var(--slate-400)" strokeWidth="1.5" strokeLinejoin="round" />
                <line x1="12" y1="18" x2="12" y2="12" stroke="var(--slate-400)" strokeWidth="1.5" strokeLinecap="round" />
                <line x1="9" y1="15" x2="15" y2="15" stroke="var(--slate-400)" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </div>
            <p className="text-sm font-medium text-ink">No CV uploaded yet</p>
            <p className="mt-1 text-xs text-slate-500">Your coach needs your CV to personalise every question to your background.</p>
          </div>
          <DropZone
            uploading={uploading}
            drag={drag}
            setDrag={setDrag}
            fileInput={fileInput}
            onFile={upload}
          />
        </Card>
      )}

      {hasCv && (
        <Button
          variant="primary"
          className="w-full"
          onClick={() => navigate(`/invite/${token}/setup`)}
        >
          Continue to session setup →
        </Button>
      )}
    </CandidateFlow>
  );
}

function DropZone({
  uploading,
  drag,
  setDrag,
  fileInput,
  onFile,
}: {
  uploading: boolean;
  drag: boolean;
  setDrag: (v: boolean) => void;
  fileInput: React.RefObject<HTMLInputElement>;
  onFile: (f: File) => void;
}) {
  return (
    <>
      <div
        onClick={() => !uploading && fileInput.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          const f = e.dataTransfer.files?.[0];
          if (f && !uploading) onFile(f);
        }}
        className={`cursor-pointer rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors ${
          drag ? "border-accent bg-accent-50" : "border-slate-300 bg-paper-2 hover:border-accent"
        } ${uploading ? "pointer-events-none opacity-60" : ""}`}
      >
        {uploading ? (
          <div className="flex items-center justify-center gap-2">
            <Spinner size={16} />
            <span className="text-sm text-slate-500">Uploading…</span>
          </div>
        ) : (
          <>
            <div className="text-sm font-medium text-ink">Drop your CV here, or click to browse</div>
            <div className="mt-1 text-xs text-slate-500">PDF or Word · max 10 MB</div>
          </>
        )}
      </div>
      <input
        ref={fileInput}
        type="file"
        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = "";
        }}
      />
    </>
  );
}
